'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  AssignClaireFailuresBody,
  AssignClaireFailuresOutput,
  ClaireFailureGroupsReport,
  UpdateClaireFailureGroupBody,
} from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

export const fetchClaireFailureGroups = (
  options: { refresh?: boolean } = {},
  signal?: AbortSignal
) =>
  apiClient.get<ClaireFailureGroupsReport>(
    `admin-terminal/claire-failure-groups${options.refresh ? '?refresh=true' : ''}`,
    { signal }
  );

export const claireFailureGroupsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.claireFailureGroups(),
    queryFn: ({ signal }) => fetchClaireFailureGroups({}, signal),
    // The API caches Langfuse for five minutes; the groups are always fresh.
    staleTime: 60_000,
  });

export const useClaireFailureGroups = () =>
  useQuery(claireFailureGroupsQueryOptions());

/** Re-read Langfuse now, past the API's cache. */
export const useRefreshClaireFailureGroups = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => fetchClaireFailureGroups({ refresh: true }),
    onSuccess: (report) =>
      queryClient.setQueryData(
        queryKeys.adminTerminal.claireFailureGroups(),
        report
      ),
    onError: (error: Error) =>
      toast.error(error.message || 'Could not refresh the failure groups'),
  });
  return { refresh: mutation.mutate, isRefreshing: mutation.isPending };
};

const useInvalidateGroups = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.adminTerminal.claireFailureGroups(),
    });
};

/** Pin confirmed replies to an existing group, or start a new one. */
export const useAssignClaireFailures = (options?: {
  onSuccess?: (result: AssignClaireFailuresOutput) => void;
}) => {
  const invalidate = useInvalidateGroups();
  const mutation = useMutation({
    mutationFn: (body: AssignClaireFailuresBody) =>
      apiClient.post<AssignClaireFailuresOutput>(
        'admin-terminal/claire-failure-groups/assign',
        body
      ),
    onSuccess: (result) => {
      void invalidate();
      toast.success(
        result.reopened
          ? 'Added, and the group was reopened: its cause came back'
          : `Added ${result.added} ${result.added === 1 ? 'reply' : 'replies'} to the group`
      );
      if (result.alreadyGrouped.length)
        toast.message(
          `${result.alreadyGrouped.length} already in a group, left where they were`
        );
      options?.onSuccess?.(result);
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not add to the group'),
  });
  return {
    assignFailures: mutation.mutate,
    isAssigning: mutation.isPending,
  };
};

export const useUpdateClaireFailureGroup = (options?: {
  onSuccess?: () => void;
}) => {
  const invalidate = useInvalidateGroups();
  const mutation = useMutation({
    mutationFn: ({
      id,
      ...body
    }: UpdateClaireFailureGroupBody & { id: string }) =>
      apiClient.patch(
        `admin-terminal/claire-failure-groups/${encodeURIComponent(id)}`,
        body
      ),
    onSuccess: () => {
      void invalidate();
      toast.success('Group updated');
      options?.onSuccess?.();
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not update the group'),
  });
  return {
    updateGroup: mutation.mutate,
    isUpdating: mutation.isPending,
  };
};

/** Take a reply out of its group; it returns to the inbox. */
export const useUnassignClaireFailure = () => {
  const invalidate = useInvalidateGroups();
  const mutation = useMutation({
    mutationFn: (observationId: string) =>
      apiClient.delete(
        `admin-terminal/claire-failure-groups/members/${encodeURIComponent(observationId)}`
      ),
    onSuccess: () => {
      void invalidate();
      toast.success('Taken out of the group');
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not take it out of the group'),
  });
  return {
    unassignFailure: mutation.mutate,
    isUnassigning: mutation.isPending,
  };
};
